import React, { useEffect, useRef } from "react";
import Pickr from "@simonwep/pickr";
import "@simonwep/pickr/dist/themes/nano.min.css";

const toHex = (color) => color.toHEXA().toString().slice(0, 7).toLowerCase();

/**
 * Accent colour picker built on Pickr. Dragging updates the colour live via
 * onChange (so the rest of the canvas follows), Save commits it, and Cancel
 * (or clicking away) reverts to the last saved colour.
 */
export default function AccentPicker({ value, onChange }) {
  const buttonRef = useRef(null);
  const pickrRef = useRef(null);
  const committedRef = useRef(value);
  const openRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const pickr = Pickr.create({
      el: buttonRef.current,
      useAsButton: true, // use our own swatch button instead of replacing it
      theme: "nano",
      default: value,
      lockOpacity: true,
      comparison: false,
      components: {
        preview: true,
        opacity: false,
        hue: true,
        interaction: {
          hex: true,
          input: true,
          cancel: true,
          save: true,
        },
      },
      i18n: { "btn:save": "Save", "btn:cancel": "Cancel" },
    });
    pickrRef.current = pickr;

    pickr.on("show", () => {
      openRef.current = true;
    });

    // Fires continuously while dragging the palette / hue slider
    pickr.on("change", (color) => {
      onChangeRef.current(toHex(color));
    });

    pickr.on("save", (color) => {
      if (color) {
        committedRef.current = toHex(color);
        onChangeRef.current(committedRef.current);
      }
      pickr.hide();
    });

    pickr.on("cancel", () => {
      pickr.hide();
    });

    // Runs after save, cancel, or clicking outside: anything unsaved is
    // reverted to the last committed colour.
    pickr.on("hide", () => {
      openRef.current = false;
      onChangeRef.current(committedRef.current);
      pickr.setColor(committedRef.current, true);
    });

    return () => {
      pickr.destroyAndRemove();
      pickrRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep Pickr in sync when the form value changes from outside (e.g. the
  // room finishes loading) and remember it as the committed colour.
  useEffect(() => {
    const pickr = pickrRef.current;
    if (!pickr) {
      return;
    }
    if (!openRef.current) {
      committedRef.current = value;
    }
    if (toHex(pickr.getColor()) !== (value || "").toLowerCase()) {
      pickr.setColor(value, true); // silent, so no change event loop
    }
  }, [value]);

  return (
    <button
      type="button"
      ref={buttonRef}
      aria-label="Accent colour"
      className="sales-room-canvas__accent-swatch"
      style={{ background: value }}
    />
  );
}